<template>
  <div class="community-selector">
    <div class="general-container">
      <div class="content-column">
        <!-- Log Out — top left -->
        <div class="logout-row">
          <div class="logout-btn" @click="$parent.logout()">
            <v-icon size="16" color="#D00808">fa-regular fa-power-off</v-icon>
            <div class="logout-text">{{ $t('ib.communities.logout') }}</div>
          </div>
        </div>

        <!-- Center content -->
        <div class="center-content">
          <!-- Partner branding -->
          <div class="partner-row">
            <span class="brought-by">Brought by</span>
            <img
              v-if="partnerLogo"
              :src="partnerLogo"
              class="partner-logo"
              alt="Partner"
            />
            <span v-else class="partner-name">{{ partnerName || 'Partner' }}</span>
          </div>

          <!-- Title -->
          <h2 class="main-title">You are already signed in this communities</h2>

          <!-- Community Cards -->
          <div class="cards-container">
            <div
              v-for="membership in communities"
              :key="membership.id"
              class="community-card"
              @click="selectCommunity(membership)"
            >
              <div class="card-inner">
                <!-- Left: avatar + name -->
                <div class="card-left">
                  <div class="avatar-wrapper" :style="{ borderColor: '#' + (membership.club.primaryColor || 'C03131') }">
                    <img
                      v-if="membership.club.imageUrl"
                      :src="membership.club.imageUrl"
                      class="avatar-img"
                    />
                    <span v-else class="avatar-fallback">{{ getInitials(membership.club.name) }}</span>
                  </div>
                  <span class="club-name">{{ membership.club.name }}</span>
                </div>

                <!-- Right: role badge + external link -->
                <div class="card-right">
                  <div
                    class="role-badge"
                    :class="'role-' + membership.rol.toLowerCase()"
                  >
                    <div class="role-label">{{ getRolLabel(membership.rol) }}</div>
                    <v-icon
                      v-if="membership.rol === 'ADMIN' || membership.rol === 'FOUNDER'"
                      size="16"
                      :color="membership.rol === 'ADMIN' ? '#C03131' : '#FF7917'"
                      class="role-icon"
                    >fa-regular fa-star</v-icon>
                  </div>
                  <div class="external-link-btn">
                    <v-icon size="16" color="#333">fa-regular fa-external-link</v-icon>
                  </div>
                </div>
              </div>
            </div>

            <!-- Empty state -->
            <div v-if="communities.length === 0 && !loading" class="empty-state">
              <v-icon size="48" color="#EBEBEB">fa-regular fa-users-slash</v-icon>
              <p>{{ $t('ib.communities.noCommunities') }}</p>
            </div>

            <!-- Loading -->
            <div v-if="loading" class="loading-state">
              <v-progress-circular indeterminate color="#3B3B3B" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ClubRepository from '@/repositories/club'

export default {
  name: 'CommunitySelector',

  data() {
    return {
      loading: false
    }
  },

  computed: {
    communities() {
      return this.$store.getters['ib/userCommunities'] || []
    },

    partnerLogo() {
      return this.$store.getters['ib/partnerLogoUrl']
    },

    partnerName() {
      return this.$store.getters['ib/partnerName']
    }
  },

  async created() {
    await this.loadCommunities()
  },

  methods: {
    async loadCommunities() {
      this.loading = true
      try {
        const response = await ClubRepository.allClubsOfUser()
        if (response.status < 400 && response.data) {
          this.$store.dispatch('ib/setUserCommunities', response.data)

          // Auto-select if only one community
          if (response.data.length === 1) {
            this.selectCommunity(response.data[0])
          }
        }
      } catch (error) {
        console.error('[CommunitySelector] Failed to load communities:', error)
      } finally {
        this.loading = false
      }
    },

    selectCommunity(membership) {
      this.$store.dispatch('ib/selectCommunity', {
        community: membership.club,
        membership: { id: membership.id, rol: membership.rol }
      })
      this.$store.commit('setClubMember', membership)
      this.$router.push('/dashboard')
    },

    getInitials(name) {
      if (!name) return '?'
      return name.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase()
    },

    getRolLabel(rol) {
      const labels = {
        FOUNDER: 'Owner',
        ADMIN: 'Admin',
        MEMBER: 'Trader'
      }
      return labels[rol] || 'Trader'
    }
  }
}
</script>

<style lang="scss" scoped>
.community-selector {
  background-color: #F8F8F8;
  min-height: 100vh;
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: center;
}

.general-container {
  flex: 1;
  min-height: 100vh;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 40px;
}

.content-column {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 40px;
  height: 100%;
}

// Log Out — top left
.logout-row {
  width: 100%;
  display: flex;
  align-items: flex-start;
}

.logout-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;

  &:hover {
    opacity: 0.7;
  }
}

.logout-text {
  font-family: 'Avenir', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 1.5;
  letter-spacing: 0.28px;
  color: #D00808;
}

// Center content
.center-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 40px;
  width: 100%;
}

// Partner branding row
.partner-row {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
}

.brought-by {
  font-family: 'Aeonik', sans-serif;
  font-weight: 500;
  font-size: 18px;
  line-height: 1.5;
  color: #3B3B3B;
}

.partner-logo {
  max-width: 220px;
  max-height: 50px;
  object-fit: contain;
}

.partner-name {
  font-family: 'Aeonik', sans-serif;
  font-weight: 700;
  font-size: 30px;
  line-height: 40px;
  color: #3B3B3B;
}

// Main title
.main-title {
  font-family: 'Aeonik', sans-serif !important;
  font-weight: 700 !important;
  font-size: 30px !important;
  line-height: 40px !important;
  color: #3B3B3B;
  text-align: center;
  width: 100%;
}

// Community cards
.cards-container {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 768px;
  max-width: 100%;
}

.community-card {
  background: #F8F8F8;
  border: 1px solid #EBEBEB;
  border-radius: 10px;
  padding: 31px 34px;
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    border-color: #ABABAB;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }
}

.card-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.card-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.avatar-wrapper {
  width: 67px;
  height: 67px;
  border-radius: 50%;
  border: 2px solid #C03131;
  overflow: hidden;
  flex-shrink: 0;
}

.avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
}

.avatar-fallback {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Aeonik', sans-serif;
  font-weight: 700;
  font-size: 18px;
  color: #3B3B3B;
  background: #EBEBEB;
}

.club-name {
  font-family: 'Aeonik', sans-serif !important;
  font-weight: 500;
  font-size: 18px;
  line-height: 1.5;
  color: #14161B;
}

.card-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

// Role badges
.role-badge {
  display: flex;
  align-items: center;
  gap: 1px;
  height: 28px;
  padding: 3px 8px;
  border-radius: 30px;
}

.role-label {
  font-family: 'Avenir', sans-serif;
  font-size: 14px;
  line-height: 1.5;
}

.role-icon {
  margin-left: 2px;
}

// Admin badge — red outlined
.role-admin {
  border: 1px solid #C03131;
  background: transparent;

  .role-label {
    font-weight: 800;
    color: #C03131;
  }
}

// Owner / Founder badge — orange filled
.role-founder {
  background: #FFF1D3;
  border: none;

  .role-label {
    font-weight: 500;
    color: #FF7917;
    letter-spacing: 0.28px;
  }
}

// Trader / Member badge — blue filled
.role-member {
  background: #D7F7FF;
  border: none;

  .role-label {
    font-weight: 500;
    color: #2196F3;
    letter-spacing: 0.28px;
  }
}

// External link button
.external-link-btn {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 5px;
  cursor: pointer;

  &:hover {
    background: rgba(0, 0, 0, 0.04);
  }
}

// Empty / loading states
.empty-state {
  text-align: center;
  padding: 40px 20px;
  color: #ABABAB;

  p {
    margin-top: 12px;
    font-family: 'Avenir', sans-serif;
    font-size: 14px;
  }
}

.loading-state {
  display: flex;
  justify-content: center;
  padding: 40px;
}

// Responsive
@media (max-width: 840px) {
  .cards-container {
    width: 100%;
  }

  .community-card {
    padding: 20px 24px;
  }

  .avatar-wrapper {
    width: 48px;
    height: 48px;
  }

  .club-name {
    font-size: 15px;
  }

  .main-title {
    font-size: 22px !important;
    line-height: 30px !important;
  }

  .partner-name {
    font-size: 22px;
    line-height: 30px;
  }
}

@media (max-width: 600px) {
  .general-container {
    padding: 20px;
  }

  .partner-row {
    flex-direction: column;
    gap: 12px;
  }

  .partner-text {
    align-items: center;
    text-align: center;
  }
}
</style>
